import React, { useEffect, useState, useRef, useCallback } from 'react';
import { ArrowRight, Instagram, Sparkles, Target, TrendingUp, Users, Zap, MessageCircle, Send, CheckCircle, Loader2, Play, ExternalLink, X, ChevronLeft, ChevronRight, Camera, PenTool, Menu, Grid3x3 as Grid, BarChart3, Calendar, Clipboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import mockData from '../data/mock';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Shuffle helper
function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

// Shuffled data (stable per page load)
const shuffledVideos = shuffle(mockData.videos);
const shuffledArtPosts = shuffle(mockData.artPosts);

// Swipe hook
function useSwipe(onLeft, onRight) {
  const touchStart = useRef(null);
  const handlers = {
    onTouchStart: (e) => { touchStart.current = e.touches[0].clientX; },
    onTouchEnd: (e) => {
      if (touchStart.current === null) return;
      const diff = touchStart.current - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) { diff > 0 ? onLeft() : onRight(); }
      touchStart.current = null;
    },
  };
  return handlers;
}

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', company: '', plan: '', message: '' });
  const [formStatus, setFormStatus] = useState('idle');
  const [activeVideo, setActiveVideo] = useState(null);
  const [thumbnails, setThumbnails] = useState({});
  const [carouselIndex, setCarouselIndex] = useState(0);
  const carouselRef = useRef(null);
  const [artThumbnails, setArtThumbnails] = useState({});
  const [artCarouselIndex, setArtCarouselIndex] = useState(0);

  const totalClientSlides = mockData.clients.length + 1; // +1 for "e muito mais"
  const totalArtSlides = shuffledArtPosts.length + 1; // +1 for final card

  const clientSwipe = useSwipe(
    () => setCarouselIndex(i => Math.min(totalClientSlides - 2, i + 1)),
    () => setCarouselIndex(i => Math.max(0, i - 1))
  );
  const artSwipe = useSwipe(
    () => setArtCarouselIndex(i => Math.min(totalArtSlides - 2, i + 1)),
    () => setArtCarouselIndex(i => Math.max(0, i - 1))
  );

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchThumbnails = async () => {
      try {
        const urls = shuffledVideos.map(v => v.url);
        const res = await fetch(`${API_URL}/api/thumbnails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ urls }),
        });
        if (res.ok) {
          const data = await res.json();
          // API now returns relative paths like /api/thumbnail-img/{hash}
          const resolved = {};
          for (const [key, value] of Object.entries(data)) {
            if (value) {
              resolved[key] = `${API_URL}${value}`;
            }
          }
          setThumbnails(resolved);
        }
      } catch (err) {
        console.error('Failed to fetch thumbnails:', err);
      }
    };
    fetchThumbnails();
  }, []);

  useEffect(() => {
    const fetchArtThumbnails = async () => {
      try {
        const res = await fetch(`${API_URL}/api/thumbnails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ urls: shuffledArtPosts }),
        });
        if (res.ok) {
          const data = await res.json();
          const resolved = {};
          for (const [key, value] of Object.entries(data)) {
            if (value) resolved[key] = `${API_URL}${value}`;
          }
          setArtThumbnails(resolved);
        }
      } catch (err) {
        console.error('Failed to fetch art thumbnails:', err);
      }
    };
    fetchArtThumbnails();
  }, []);

  // Animated counter hook
  function useCounter(end, duration = 1500) {
    const [count, setCount] = useState(0);
    const [started, setStarted] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
      const obs = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting && !started) setStarted(true);
      }, { threshold: 0.5 });
      if (ref.current) obs.observe(ref.current);
      return () => obs.disconnect();
    }, [started]);

    useEffect(() => {
      if (!started) return;
      let start = 0;
      const step = Math.ceil(end / (duration / 16));
      const timer = setInterval(() => {
        start += step;
        if (start >= end) { setCount(end); clearInterval(timer); }
        else setCount(start);
      }, 16);
      return () => clearInterval(timer);
    }, [started, end, duration]);

    return { count, ref };
  }

  const counter1 = useCounter(20);
  const counter2 = useCounter(1);
  const counter3 = useCounter(5);
  const [hoveredPlan, setHoveredPlan] = useState(null);

  const planIcons = {
    Grid: <Grid className="w-4 h-4" />,
    MessageCircle: <MessageCircle className="w-4 h-4" />,
    Target: <Target className="w-4 h-4" />,
    Camera: <Camera className="w-4 h-4" />,
    Calendar: <Calendar className="w-4 h-4" />,
    BarChart3: <BarChart3 className="w-4 h-4" />,
    Users: <Users className="w-4 h-4" />,
    Sparkles: <Sparkles className="w-4 h-4" />,
    Clipboard: <Clipboard className="w-4 h-4" />,
    Zap: <Zap className="w-4 h-4" />,
  };

  // Scroll reveal
  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach(e => {
        if (e.isIntersecting) {
          e.target.classList.add('revealed');
          obs.unobserve(e.target);
        }
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    document.querySelectorAll('.reveal-on-scroll').forEach(el => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/5521999062313', '_blank');
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) return;
    setFormStatus('loading');
    try {
      const res = await fetch(`${API_URL}/api/leads`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (res.ok) {
        setFormStatus('success');
        const msg = encodeURIComponent(`Olá! Meu nome é ${formData.name}${formData.company ? ` da ${formData.company}` : ''}. ${formData.message || 'Gostaria de saber mais sobre os serviços da Milyan.'}`);
        setTimeout(() => {
          window.open(`https://wa.me/5521999062313?text=${msg}`, '_blank');
        }, 1500);
        setTimeout(() => {
          setFormData({ name: '', phone: '', email: '', company: '', plan: '', message: '' });
          setFormStatus('idle');
        }, 4000);
      } else {
        setFormStatus('idle');
      }
    } catch {
      setFormStatus('idle');
    }
  };

  const getEmbedUrl = (video) => {
    if (video.type === 'story') return null;
    return video.url.endsWith('/') ? `${video.url}embed/` : `${video.url}/embed/`;
  };

  const handleVideoClick = (video) => {
    const embedUrl = getEmbedUrl(video);
    if (embedUrl) {
      setActiveVideo({ ...video, embedUrl });
    } else {
      window.open(video.url, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-raisin-black text-white">
      {/* Video Modal */}
      {activeVideo && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm"
          data-testid="video-modal-overlay"
          onClick={() => setActiveVideo(null)}
        >
          <div
            className="relative w-[90vw] max-w-[420px] aspect-[9/16] bg-raisin-black rounded-2xl overflow-hidden border border-slate-blue/30 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setActiveVideo(null)}
              data-testid="video-modal-close"
              className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-raisin-black/80 border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
            >
              <X className="w-5 h-5 text-white" />
            </button>
            <iframe
              src={activeVideo.embedUrl}
              className="w-full h-full border-0"
              allowFullScreen
              allow="autoplay; encrypted-media"
              title="Instagram Reel"
              data-testid="video-modal-iframe"
            />
            <a
              href={activeVideo.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="video-modal-instagram-link"
              className="absolute bottom-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-2 bg-raisin-black/80 backdrop-blur-sm border border-slate-blue/30 rounded-full px-4 py-2 text-sm text-gray-300 hover:text-chartreuse transition-colors"
            >
              <Instagram className="w-4 h-4" />
              Abrir no Instagram
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      )}
      {/* Header/Navbar */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-raisin-black/95 backdrop-blur-md border-b border-slate-blue/20' : 'bg-raisin-black/80 backdrop-blur-sm'}`}>
        <nav className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center">
            <img 
              src="https://customer-assets.emergentagent.com/job_milyan-strategy/artifacts/usg5y30p_Logo%20Milyan-30.png" 
              alt="Milyan Marketing" 
              className="h-20 md:h-[90px] w-auto"
            />
          </div>
          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            <a href="#sobre" className="nav-link">Sobre</a>
            <a href="#servicos" className="nav-link">Serviços</a>
            <a href="#planos" className="nav-link">Planos</a>
            <a href="#portfolio" className="nav-link">Portfólio</a>
            <Button 
              onClick={handleWhatsAppClick}
              className="cta-button bg-slate-blue hover:bg-slate-blue/90"
            >
              Falar com a Milyan
            </Button>
          </div>
          {/* Mobile hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg border border-slate-blue/30 hover:bg-slate-blue/10 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </nav>
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-raisin-black/98 backdrop-blur-md border-t border-slate-blue/20 px-6 py-4 space-y-1" data-testid="mobile-menu">
            {['sobre', 'servicos', 'planos', 'portfolio', 'contato'].map((id) => (
              <a
                key={id}
                href={`#${id}`}
                onClick={() => setMobileMenuOpen(false)}
                className="block py-3 px-4 text-gray-300 hover:text-chartreuse hover:bg-white/5 rounded-lg transition-colors capitalize"
              >
                {id === 'servicos' ? 'Serviços' : id === 'planos' ? 'Planos' : id === 'portfolio' ? 'Portfólio' : id.charAt(0).toUpperCase() + id.slice(1)}
              </a>
            ))}
            <Button 
              onClick={() => { handleWhatsAppClick(); setMobileMenuOpen(false); }}
              className="w-full mt-2 bg-chartreuse text-raisin-black hover:bg-chartreuse/90 font-bold"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Falar com a Milyan
            </Button>
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section className="hero-section min-h-screen flex items-center justify-center relative overflow-hidden pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-raisin-black via-raisin-black to-slate-blue/20"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-slate-blue rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-chartreuse rounded-full blur-3xl"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-block mb-6 px-4 py-2 bg-slate-blue/20 rounded-full border border-slate-blue/40 backdrop-blur-sm">
              <span className="text-chartreuse text-sm font-mono uppercase tracking-wider">Marketing Estratégico</span>
            </div>
            
            <h1 className="hero-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              Sua empresa merece
              <br />mais do que aparecer.
              <br />Merece <span className="text-chartreuse">crescer</span>.
            </h1>
            
            <p className="body-large text-xl md:text-2xl text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed">
              Na Milyan, unimos <mark className="highlight-text">estratégia, criatividade e execução</mark> para transformar marcas em negócios que crescem de forma consistente.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Button 
                onClick={handleWhatsAppClick}
                size="lg"
                className="cta-button bg-chartreuse text-raisin-black hover:bg-chartreuse/90 px-8 py-6 text-lg font-bold group"
              >
                Quero crescer minha marca
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                variant="outline"
                size="lg"
                className="border-slate-blue text-slate-blue hover:bg-slate-blue/10 px-8 py-6 text-lg"
                onClick={() => document.getElementById('portfolio').scrollIntoView({ behavior: 'smooth' })}
              >
                Ver cases de sucesso
              </Button>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-slate-blue rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-chartreuse rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Sobre a Milyan */}
      <section id="sobre" className="py-24 relative reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-slate-blue to-chartreuse opacity-20 blur-2xl rounded-3xl"></div>
                <img 
                  src="https://customer-assets-lxgj4vgw.emergentagent.net/job_848286f8-5d7d-4e07-b946-7ff0281af2a5/artifacts/gty9pwbd_Design%20sem%20nome%20%282%29.png" 
                  alt="Equipe Milyan"
                  loading="lazy"
                  className="relative rounded-2xl shadow-2xl border border-slate-blue/20 w-full md:min-h-[400px] md:object-cover"
                />
              </div>
              
              <div>
                <h2 className="section-heading text-4xl md:text-5xl font-bold mb-6">
                  Sobre <span className="text-slate-blue">Nós</span>
                </h2>
                
                <div className="space-y-4 text-gray-300 leading-relaxed">
                  <p className="text-lg">
                    A Milyan é uma agência de marketing que une <mark className="highlight-text">criatividade, estratégia e execução</mark> para transformar marcas em negócios que crescem com consistência.
                  </p>
                  
                  <p className="text-lg">
                    Mais do que presença digital, <mark className="highlight-text">construímos posicionamento</mark>. Mais do que conteúdo, <mark className="highlight-text">geramos percepção, autoridade e resultado</mark>.
                  </p>
                  
                  <p className="text-lg">
                    Somos uma agência jovem, com visão moderna de mercado, mas com <mark className="highlight-text">maturidade estratégica</mark> para atuar ao lado de empresas que não buscam apenas visibilidade, mas <mark className="highlight-text">crescimento real, estrutura e faturamento</mark>.
                  </p>
                </div>
                
                <div className="mt-8 grid grid-cols-3 gap-3 md:gap-6">
                  <div ref={counter1.ref} className="text-center p-3 md:p-4 bg-slate-blue/10 rounded-xl border border-slate-blue/20">
                    <div className="text-2xl md:text-3xl font-black text-chartreuse mb-1">+{counter1.count}</div>
                    <div className="text-xs md:text-sm text-gray-400">Empresas atendidas</div>
                  </div>
                  <div ref={counter2.ref} className="text-center p-3 md:p-4 bg-giants-orange/10 rounded-xl border border-giants-orange/20">
                    <div className="text-2xl md:text-3xl font-black text-giants-orange mb-1">+{counter2.count} MI</div>
                    <div className="text-xs md:text-sm text-gray-400">Contas alcançadas</div>
                  </div>
                  <div ref={counter3.ref} className="text-center p-3 md:p-4 bg-chartreuse/10 rounded-xl border border-chartreuse/20">
                    <div className="text-2xl md:text-3xl font-black text-chartreuse mb-1">+{counter3.count}</div>
                    <div className="text-xs md:text-sm text-gray-400">Anos atuando</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Serviços */}
      <section id="servicos" className="py-24 bg-gradient-to-b from-transparent to-slate-blue/5 reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-4">
                Nossos <span className="text-slate-blue">Serviços</span>
              </h2>
              <p className="text-xl text-gray-400 max-w-2xl mx-auto">
                Soluções completas para transformar sua marca em um negócio que cresce com consistência
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {mockData.services.map((service, index) => (
                <Card 
                  key={index}
                  className="service-card p-6 bg-raisin-black border-slate-blue/20 hover:border-slate-blue/60 transition-all duration-300 group"
                >
                  <div className="mb-4 inline-flex p-3 bg-slate-blue/10 rounded-xl group-hover:bg-slate-blue/20 transition-colors">
                    {service.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3 group-hover:text-chartreuse transition-colors">
                    {service.title}
                  </h3>
                  <p className="text-gray-400 leading-relaxed">
                    {service.description}
                  </p>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Planos */}
      <section id="planos" className="py-24 relative reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">

            {/* Social Media */}
            <div className="text-center mb-16">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-2">
                Planos de <span className="text-slate-blue">Investimento</span>
              </h2>
              <p className="text-2xl font-bold text-chartreuse tracking-wide uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                Social Media
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 mb-20">
              {mockData.plans.map((plan, index) => (
                <div
                  key={index}
                  data-testid={`social-plan-${index}`}
                  onMouseEnter={() => setHoveredPlan(index)}
                  onMouseLeave={() => setHoveredPlan(null)}
                  className={`relative rounded-2xl border ${plan.borderColor} bg-gradient-to-b ${plan.gradient} p-1 transition-all duration-500 ${
                    hoveredPlan !== null && hoveredPlan !== index ? 'opacity-70 scale-[0.98]' : ''
                  } ${hoveredPlan === index ? 'scale-[1.03]' : ''}`}
                  style={hoveredPlan === index ? {
                    boxShadow: plan.glowColor === 'chartreuse' ? '0 0 40px rgba(218,255,2,0.2), 0 0 80px rgba(218,255,2,0.1)' :
                               plan.glowColor === 'giants-orange' ? '0 0 40px rgba(254,87,42,0.2), 0 0 80px rgba(254,87,42,0.1)' :
                               '0 0 40px rgba(104,91,199,0.2), 0 0 80px rgba(104,91,199,0.1)'
                  } : {}}
                >
                  <div className="rounded-xl bg-raisin-black p-6 md:p-8 h-full flex flex-col">
                    {/* Badge */}
                    {plan.badge && (
                      <div className={`inline-flex self-start items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4 ${
                        plan.badgeColor === 'chartreuse' ? 'bg-chartreuse/15 text-chartreuse border border-chartreuse/30' :
                        'bg-slate-blue/15 text-slate-blue border border-slate-blue/30'
                      }`}>
                        <CheckCircle className="w-3 h-3" />
                        {plan.badge}
                      </div>
                    )}

                    {/* Header */}
                    <div className="mb-5">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-3xl">{plan.emoji}</span>
                        <h3 className="text-2xl md:text-3xl font-black">{plan.name}</h3>
                      </div>
                      <p className="text-sm text-gray-400 leading-relaxed">{plan.description}</p>
                    </div>

                    {/* Divider */}
                    <div className={`h-px w-full mb-6 ${
                      plan.glowColor === 'chartreuse' ? 'bg-gradient-to-r from-transparent via-chartreuse/30 to-transparent' :
                      plan.glowColor === 'giants-orange' ? 'bg-gradient-to-r from-transparent via-giants-orange/30 to-transparent' :
                      'bg-gradient-to-r from-transparent via-slate-blue/30 to-transparent'
                    }`} />

                    {/* Features */}
                    <div className="space-y-4 flex-1 mb-8">
                      {plan.features.map((f, idx) => (
                        <div key={idx} className="flex items-start gap-3">
                          <div className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center mt-0.5 ${
                            plan.glowColor === 'chartreuse' ? 'bg-chartreuse/10 text-chartreuse' :
                            plan.glowColor === 'giants-orange' ? 'bg-giants-orange/10 text-giants-orange' :
                            'bg-slate-blue/10 text-slate-blue'
                          }`}>
                            {planIcons[f.icon] || <Sparkles className="w-4 h-4" />}
                          </div>
                          <div>
                            <span className="text-white text-sm font-semibold">{f.text}</span>
                            {f.sub && <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.sub}</p>}
                          </div>
                        </div>
                      ))}
                    </div>

                    {/* CTA Button */}
                    <Button
                      onClick={handleWhatsAppClick}
                      className={`w-full py-5 font-bold text-sm relative overflow-hidden group/btn transition-all duration-300 ${
                        plan.glowColor === 'chartreuse' ? 'bg-chartreuse text-raisin-black hover:bg-chartreuse/90' :
                        plan.glowColor === 'giants-orange' ? 'bg-giants-orange text-white hover:bg-giants-orange/90' :
                        'bg-slate-blue text-white hover:bg-slate-blue/90'
                      }`}
                    >
                      <span className="relative z-10 flex items-center justify-center gap-2">
                        {plan.cta}
                        <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" />
                      </span>
                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover/btn:translate-x-full transition-transform duration-700" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Cobertura de Eventos */}
            <div className="text-center mb-16">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-2">
                Planos de <span className="text-giants-orange">Investimento</span>
              </h2>
              <p className="text-2xl font-bold text-chartreuse tracking-wide uppercase" style={{ fontFamily: "'Space Mono', monospace" }}>
                <Camera className="w-6 h-6 inline mr-2 mb-1" />
                Cobertura de Eventos
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {mockData.eventPlans.map((plan, index) => (
                <Card 
                  key={index}
                  data-testid={`event-plan-${index}`}
                  className={`pricing-card p-8 bg-gradient-to-b ${plan.gradient} border-2 ${plan.borderColor} relative overflow-hidden group hover:scale-105 transition-transform duration-300`}
                >
                  {plan.popular && (
                    <div className="absolute top-0 right-0 bg-chartreuse text-raisin-black px-4 py-1 text-xs font-bold uppercase">
                      Mais Popular
                    </div>
                  )}
                  <div className="mb-6">
                    <h3 className="text-3xl font-black">{plan.name}</h3>
                  </div>
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-chartreuse flex-shrink-0 mt-0.5" />
                        <span className="text-gray-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button 
                    onClick={handleWhatsAppClick}
                    className={`w-full ${plan.popular ? 'bg-chartreuse text-raisin-black hover:bg-chartreuse/90' : 'bg-slate-blue hover:bg-slate-blue/90'} font-bold`}
                  >
                    Falar com a Milyan
                  </Button>
                </Card>
              ))}
            </div>

            <p className="text-center text-gray-400 mt-8 text-sm">
              * Todos os planos são personalizáveis
            </p>
          </div>
        </div>
      </section>

      {/* Pacotes Avulsos */}
      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-blue/5 via-transparent to-giants-orange/5"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-2 text-chartreuse">
                Pacotes Avulsos
              </h2>
              <p className="text-lg text-gray-400">
                Serviços sob demanda para necessidades específicas
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {mockData.packages.map((pkg, index) => (
                <div
                  key={index}
                  data-testid={`package-card-${index}`}
                  onClick={handleWhatsAppClick}
                  className="group cursor-pointer relative rounded-2xl border border-slate-blue/30 bg-white/5 backdrop-blur-sm p-6 hover:border-chartreuse/50 transition-all duration-300 hover:scale-105"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-chartreuse to-giants-orange rounded-t-2xl opacity-0 group-hover:opacity-100 transition-opacity" />
                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-chartreuse transition-colors">
                    {pkg.name}
                  </h3>
                  <p className="text-sm text-gray-400 mb-4">A partir de</p>
                  <p className="text-3xl font-black text-chartreuse">
                    {pkg.price}
                  </p>
                  <div className="mt-4 flex items-center gap-1 text-sm text-gray-500 group-hover:text-chartreuse/70 transition-colors">
                    <MessageCircle className="w-4 h-4" />
                    <span>Solicitar</span>
                    <ArrowRight className="w-3 h-3 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Portfólio */}
      <section id="portfolio" className="py-24 bg-gradient-to-b from-slate-blue/5 to-transparent reveal-on-scroll">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-4">
                Resultados que <span className="text-gradient bg-gradient-to-r from-giants-orange to-chartreuse bg-clip-text text-transparent">falam por si</span>
              </h2>
              <p className="text-xl text-gray-400">
                Conheça alguns dos nossos cases de sucesso
              </p>
            </div>

            {/* Carrossel de Clientes */}
            <div className="relative mb-16">
              <div className="overflow-hidden" ref={carouselRef} {...clientSwipe}>
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${carouselIndex * (100 / 4)}%)` }}
                >
                  {mockData.clients.map((client, index) => (
                    <div
                      key={index}
                      data-testid={`client-logo-${index}`}
                      className="flex-shrink-0 w-1/2 md:w-1/4 px-3"
                    >
                      <div className="client-logo-card group rounded-xl overflow-hidden border border-slate-blue/20 hover:border-slate-blue/40 transition-all duration-300 h-[160px] md:h-[200px] relative bg-[#f0f0f0]">
                        <img
                          src={client.logo}
                          alt={client.name}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-raisin-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-4">
                          <span className="text-white font-semibold text-sm">{client.name}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                  {/* Card "e muito mais" */}
                  <div className="flex-shrink-0 w-1/2 md:w-1/4 px-3">
                    <div className="rounded-xl border border-chartreuse/30 bg-gradient-to-br from-slate-blue/10 to-chartreuse/5 h-[160px] md:h-[200px] flex flex-col items-center justify-center gap-2 md:gap-3">
                      <span className="text-3xl md:text-4xl font-black text-chartreuse">+</span>
                      <span className="text-base md:text-lg font-bold text-white">e muito mais</span>
                      <span className="text-xs md:text-sm text-gray-400 text-center px-4">Você pode ser um desses!</span>
                    </div>
                  </div>
                </div>
              </div>
              {/* Arrows */}
              <button
                onClick={() => setCarouselIndex(Math.max(0, carouselIndex - 1))}
                data-testid="carousel-prev"
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 rounded-full bg-raisin-black/80 border border-slate-blue/30 flex items-center justify-center hover:bg-slate-blue/20 transition-colors ${carouselIndex === 0 ? 'opacity-30 cursor-default' : ''}`}
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => setCarouselIndex(Math.min(totalClientSlides - 2, carouselIndex + 1))}
                data-testid="carousel-next"
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 rounded-full bg-raisin-black/80 border border-slate-blue/30 flex items-center justify-center hover:bg-slate-blue/20 transition-colors ${carouselIndex >= totalClientSlides - 2 ? 'opacity-30 cursor-default' : ''}`}
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
              {/* Dots */}
              <div className="flex justify-center gap-2 mt-6">
                {Array.from({ length: Math.max(1, totalClientSlides - 1) }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setCarouselIndex(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === carouselIndex ? 'bg-chartreuse w-6' : 'bg-slate-blue/40 hover:bg-slate-blue/60'}`}
                  />
                ))}
              </div>
            </div>

            {/* Depoimentos */}
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              {mockData.testimonials.map((testimonial, index) => (
                <Card 
                  key={index}
                  data-testid={`testimonial-${index}`}
                  className="testimonial-card p-6 bg-gradient-to-br from-raisin-black to-slate-blue/10 border-slate-blue/30"
                >
                  <div className="mb-4">
                    <span className="text-6xl text-chartreuse opacity-50">"</span>
                  </div>
                  <p className="text-gray-300 mb-6 italic">
                    {testimonial.quote}
                  </p>
                  <div className="flex items-center gap-3">
                    <img 
                      src={testimonial.photo} 
                      alt={testimonial.name}
                      className="w-14 h-14 rounded-full object-cover border-2 border-slate-blue/40"
                      loading="lazy"
                    />
                    <div>
                      <div className="font-bold">{testimonial.name}</div>
                      <div className="text-sm text-gray-400">{testimonial.company}</div>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Nossos Vídeos / Reels */}
      <section className="py-24 bg-gradient-to-b from-transparent to-slate-blue/5">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-4">
                Nosso <span className="text-gradient bg-gradient-to-r from-chartreuse to-slate-blue bg-clip-text text-transparent">Portfólio em Vídeo</span>
              </h2>
              <p className="text-lg text-gray-400">
                Veja na prática o que produzimos para nossos clientes
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {shuffledVideos.map((video, index) => (
                <div
                  key={index}
                  onClick={() => handleVideoClick(video)}
                  data-testid={`video-card-${index}`}
                  className="group relative aspect-[9/16] rounded-xl overflow-hidden border border-slate-blue/20 hover:border-chartreuse/50 transition-all duration-300 cursor-pointer"
                >
                  {thumbnails[video.url] ? (
                    <img
                      src={thumbnails[video.url]}
                      alt={`Reel ${index + 1}`}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  ) : (
                    <div className="absolute inset-0 bg-gradient-to-br from-slate-blue/30 via-raisin-black to-giants-orange/20">
                      <div className="absolute inset-0 flex items-center justify-center opacity-10">
                        <Instagram className="w-20 h-20 text-white" />
                      </div>
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-black/40 backdrop-blur-sm border border-white/30 flex items-center justify-center group-hover:bg-chartreuse/20 group-hover:border-chartreuse/50 transition-all duration-300 group-hover:scale-110">
                      <Play className="w-6 h-6 text-white group-hover:text-chartreuse fill-white group-hover:fill-chartreuse transition-colors" />
                    </div>
                    <span className="text-xs font-semibold text-white/70 uppercase tracking-wider">
                      {video.type === 'story' ? 'Story' : 'Reel'}
                    </span>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-chartreuse to-giants-orange scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
                </div>
              ))}
            </div>

            <div className="text-center mt-8">
              <a
                href="https://instagram.com/milyanmkt"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="view-all-instagram"
                className="inline-flex items-center gap-2 text-chartreuse hover:text-chartreuse/80 transition-colors font-semibold"
              >
                <Instagram className="w-5 h-5" />
                Ver tudo no Instagram @milyanmkt
                <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Portfólio de Artes */}
      <section className="py-24 bg-gradient-to-b from-transparent to-slate-blue/5">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="section-heading text-4xl md:text-5xl font-bold mb-4">
                Portfólio de <span className="text-gradient bg-gradient-to-r from-slate-blue to-chartreuse bg-clip-text text-transparent">Artes</span>
              </h2>
              <p className="text-lg text-gray-400">
                Design estratégico que fortalece marcas
              </p>
            </div>

            <div className="relative">
              <div className="overflow-hidden" {...artSwipe}>
                <div
                  className="flex transition-transform duration-500 ease-in-out"
                  style={{ transform: `translateX(-${artCarouselIndex * (100 / 4)}%)` }}
                >
                  {shuffledArtPosts.map((url, index) => (
                    <div key={index} className="flex-shrink-0 w-1/2 md:w-1/4 px-2">
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`art-card-${index}`}
                        className="group block aspect-square rounded-xl overflow-hidden border border-slate-blue/20 hover:border-chartreuse/50 transition-all duration-300 relative"
                      >
                        {artThumbnails[url] ? (
                          <img
                            src={artThumbnails[url]}
                            alt={`Arte ${index + 1}`}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-slate-blue/20 to-giants-orange/10 flex items-center justify-center">
                            <Instagram className="w-10 h-10 text-white/20" />
                          </div>
                        )}
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <ExternalLink className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </a>
                    </div>
                  ))}
                  {/* Card final */}
                  <div className="flex-shrink-0 w-1/2 md:w-1/4 px-2">
                    <div className="aspect-square rounded-xl border border-chartreuse/30 bg-gradient-to-br from-slate-blue/10 to-chartreuse/5 flex flex-col items-center justify-center gap-3 px-4">
                      <PenTool className="w-8 h-8 text-chartreuse" />
                      <span className="text-sm md:text-base font-bold text-white text-center">Essas foram apenas algumas das artes criadas aqui</span>
                    </div>
                  </div>
                </div>
              </div>
              <button
                onClick={() => setArtCarouselIndex(Math.max(0, artCarouselIndex - 1))}
                className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-2 z-10 w-10 h-10 rounded-full bg-raisin-black/80 border border-slate-blue/30 flex items-center justify-center hover:bg-slate-blue/20 transition-colors ${artCarouselIndex === 0 ? 'opacity-30' : ''}`}
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <button
                onClick={() => setArtCarouselIndex(Math.min(totalArtSlides - 2, artCarouselIndex + 1))}
                className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-2 z-10 w-10 h-10 rounded-full bg-raisin-black/80 border border-slate-blue/30 flex items-center justify-center hover:bg-slate-blue/20 transition-colors ${artCarouselIndex >= totalArtSlides - 2 ? 'opacity-30' : ''}`}
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
              <div className="flex justify-center gap-1.5 mt-6 flex-wrap max-w-xs mx-auto">
                {Array.from({ length: Math.max(1, totalArtSlides - 1) }, (_, i) => (
                  <button
                    key={i}
                    onClick={() => setArtCarouselIndex(i)}
                    className={`w-2 h-2 rounded-full transition-all duration-300 ${i === artCarouselIndex ? 'bg-chartreuse w-4' : 'bg-slate-blue/40 hover:bg-slate-blue/60'}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diferencial */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-slate-blue/10 to-giants-orange/10"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mb-8 leading-tight">
              Não somos uma agência <span className="text-slate-blue">operacional</span>.<br />
              Somos um braço <span className="text-gradient bg-gradient-to-r from-giants-orange to-chartreuse bg-clip-text text-transparent">estratégico</span> do seu negócio.
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6 mt-12">
              <div className="p-6 bg-raisin-black/80 backdrop-blur-sm rounded-xl border border-slate-blue/30">
                <Target className="w-12 h-12 text-chartreuse mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-2">Planejamento Estratégico</h3>
                <p className="text-gray-400">Análise profunda do mercado e definição de objetivos claros</p>
              </div>
              
              <div className="p-6 bg-raisin-black/80 backdrop-blur-sm rounded-xl border border-giants-orange/30">
                <TrendingUp className="w-12 h-12 text-giants-orange mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-2">Crescimento Previsível</h3>
                <p className="text-gray-400">Métricas e KPIs para acompanhar resultados reais</p>
              </div>
              
              <div className="p-6 bg-raisin-black/80 backdrop-blur-sm rounded-xl border border-chartreuse/30">
                <Zap className="w-12 h-12 text-chartreuse mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-2">Acompanhamento Contínuo</h3>
                <p className="text-gray-400">Monitoramento e otimização constante das estratégias</p>
              </div>
              
              <div className="p-6 bg-raisin-black/80 backdrop-blur-sm rounded-xl border border-slate-blue/30">
                <Users className="w-12 h-12 text-slate-blue mb-4 mx-auto" />
                <h3 className="text-xl font-bold mb-2">Direcionamento de Leads</h3>
                <p className="text-gray-400">Estratégias para converter audiência em clientes</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Final + Formulário de Leads */}
      <section id="contato" className="py-24 relative overflow-hidden reveal-on-scroll">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-blue/20 via-raisin-black to-chartreuse/20"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-5xl font-black mb-4 leading-tight">
                Pronto para <span className="text-gradient bg-gradient-to-r from-chartreuse to-giants-orange bg-clip-text text-transparent">crescer de verdade</span>?
              </h2>
              <p className="text-base md:text-lg text-gray-400">
                Preencha o formulário e nossa equipe entra em contato pelo WhatsApp
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Formulário */}
              <Card className="p-8 bg-raisin-black/90 backdrop-blur-sm border-slate-blue/30" data-testid="lead-form-card">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <input
                      data-testid="lead-name-input"
                      type="text"
                      placeholder="Seu nome *"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      data-testid="lead-phone-input"
                      type="tel"
                      placeholder="WhatsApp (com DDD) *"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <input
                      data-testid="lead-email-input"
                      type="email"
                      placeholder="E-mail"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      data-testid="lead-company-input"
                      type="text"
                      placeholder="Nome da empresa"
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors"
                    />
                  </div>
                  <div>
                    <select
                      data-testid="lead-plan-select"
                      value={formData.plan}
                      onChange={(e) => setFormData({ ...formData, plan: e.target.value })}
                      className="w-full px-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors appearance-none"
                    >
                      <option value="" className="bg-raisin-black">Plano de interesse</option>
                      <option value="Social Media - Orion" className="bg-raisin-black">Social Media - Orion</option>
                      <option value="Social Media - Sirius" className="bg-raisin-black">Social Media - Sirius</option>
                      <option value="Social Media - Milyan" className="bg-raisin-black">Social Media - Milyan</option>
                      <option value="Eventos - Orion" className="bg-raisin-black">Eventos - Orion</option>
                      <option value="Eventos - Sirius" className="bg-raisin-black">Eventos - Sirius</option>
                      <option value="Eventos - Milyan" className="bg-raisin-black">Eventos - Milyan</option>
                      <option value="Pack de Artes" className="bg-raisin-black">Pack de Artes</option>
                      <option value="Pack de Vídeos" className="bg-raisin-black">Pack de Vídeos</option>
                      <option value="Estratégia e Consultoria" className="bg-raisin-black">Estratégia e Consultoria</option>
                      <option value="Gestão de Tráfego" className="bg-raisin-black">Gestão de Tráfego</option>
                    </select>
                  </div>
                  <div>
                    <textarea
                      data-testid="lead-message-input"
                      placeholder="Conte um pouco sobre seu negócio..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={3}
                      className="w-full px-4 py-3 bg-white/5 border border-slate-blue/30 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-chartreuse/50 transition-colors resize-none"
                    />
                  </div>
                  <Button
                    type="submit"
                    data-testid="lead-submit-button"
                    disabled={formStatus === 'loading' || formStatus === 'success'}
                    className={`w-full py-6 text-lg font-bold transition-all duration-300 ${
                      formStatus === 'success'
                        ? 'bg-green-600 text-white'
                        : 'bg-gradient-to-r from-chartreuse to-giants-orange text-raisin-black hover:opacity-90'
                    }`}
                  >
                    {formStatus === 'loading' && <Loader2 className="mr-2 w-5 h-5 animate-spin" />}
                    {formStatus === 'success' && <CheckCircle className="mr-2 w-5 h-5" />}
                    {formStatus === 'idle' && <Send className="mr-2 w-5 h-5" />}
                    {formStatus === 'loading' ? 'Enviando...' : formStatus === 'success' ? 'Enviado! Redirecionando...' : 'Enviar e falar no WhatsApp'}
                  </Button>
                </form>
              </Card>

              {/* Texto motivacional */}
              <div className="flex flex-col justify-center space-y-6">
                <h3 className="text-xl md:text-3xl font-black leading-tight">
                  Se você quer parar de apenas <span className="line-through text-gray-500">postar</span> e começar a <span className="text-gradient bg-gradient-to-r from-chartreuse to-giants-orange bg-clip-text text-transparent">crescer</span>, a Milyan é o próximo passo.
                </h3>
                <p className="text-gray-400 text-base md:text-lg">
                  Nossa equipe analisa seu negócio e monta uma estratégia personalizada para gerar resultados reais.
                </p>
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-chartreuse flex-shrink-0" />
                    <span className="text-gray-300">Resposta em até 24h</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-chartreuse flex-shrink-0" />
                    <span className="text-gray-300">Diagnóstico gratuito do seu marketing</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-chartreuse flex-shrink-0" />
                    <span className="text-gray-300">Sem compromisso — vamos conversar primeiro</span>
                  </div>
                </div>
                <Button 
                  onClick={handleWhatsAppClick}
                  data-testid="cta-whatsapp-direct"
                  variant="outline"
                  size="lg"
                  className="border-chartreuse/50 text-chartreuse hover:bg-chartreuse/10 w-fit mt-2 group"
                >
                  <MessageCircle className="mr-2 w-5 h-5" />
                  Ou fale direto no WhatsApp
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-slate-blue/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-col items-center gap-6">
            <div className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_milyan-strategy/artifacts/bqk23oji_Logo%20Milyan-33.png" 
                alt="Milyan Marketing" 
                className="h-14 w-auto"
                data-testid="footer-logo"
              />
            </div>
            
            <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6">
              <a href="#sobre" className="text-gray-400 hover:text-chartreuse transition-colors text-sm md:text-base">Sobre</a>
              <a href="#servicos" className="text-gray-400 hover:text-chartreuse transition-colors text-sm md:text-base">Serviços</a>
              <a href="#planos" className="text-gray-400 hover:text-chartreuse transition-colors text-sm md:text-base">Planos</a>
              <a href="#portfolio" className="text-gray-400 hover:text-chartreuse transition-colors text-sm md:text-base">Portfólio</a>
              <a href="#contato" className="text-gray-400 hover:text-chartreuse transition-colors text-sm md:text-base">Contato</a>
            </div>
            
            <a href="https://instagram.com/milyanmkt" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-chartreuse transition-colors" data-testid="footer-instagram-link">
              <Instagram className="w-6 h-6" />
            </a>
          </div>
          
          <div className="text-center mt-8 text-gray-500 text-sm">
            © {new Date().getFullYear()} Milyan Marketing. Todos os direitos reservados.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
